import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { SectionHeading } from './SectionHeading';
type Engagement = {
  number: string;
  title: string;
  body: string;
  annotation: string;
  image: string;
  alt: string;
};
const engagements: Engagement[] = [{
  number: '01',
  title: 'A working system, built at your desks',
  body: "Our engineers move into your office — not a war room down the hall, but the desks where the work actually happens. They sit with the people who own the problem, plug into your live data, and start building against it from day one. Progress isn't a status update; it's a system that does a little more real work each week. By week six it's in production — not a scoping document.",
  annotation: 'On-site build → live system in six weeks',
  image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1600&auto=format&fit=crop',
  alt: 'Engineer working at a desk alongside a client team'
}, {
  number: '02',
  title: 'Engineers you\u2019d hire yourself',
  body: 'Most AI hiring fails because nobody on the hiring side has built these systems before. We have. When the work needs more hands, we find the specific people — model builders, infrastructure engineers, governance leads — and vet them the way we vet our own: by looking at what they\u2019ve shipped. One specialist or a full team, matched to your roadmap and your culture.',
  annotation: 'AI Solutions · Infrastructure · Governance',
  image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=1600&auto=format&fit=crop',
  alt: 'Small team of engineers collaborating around a laptop'
}, {
  number: '03',
  title: 'Built into your stack, not bolted on',
  body: "A system that lives in a vendor's cloud, behind a vendor's API, is a system you rent. Ours lives in your codebase, your pipelines, your operations. Before we hand it over, your team learns to run it, extend it, and measure it — so the value keeps compounding after we leave, and the next project starts from higher ground.",
  annotation: 'Runs in your stack. Shows up in your numbers.',
  image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop',
  alt: 'Team reviewing dashboards and metrics on a screen'
}];
export function HowWeEngage() {
  return <section id="how-we-engage" className="py-20 md:py-28 border-t border-border" data-id="element-59">
      <div className="container" data-id="element-60">
        <SectionHeading eyebrow="How we engage" title="Three ways in. Every one ends with software doing real work." align="center" className="max-w-3xl" data-id="element-61" />
        <div className="mt-14 md:mt-20 space-y-16 md:space-y-24" data-id="element-62">
          {engagements.map((item, i) => <motion.article key={item.number} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`} data-id="element-63">
              <div data-id="element-64">
                <span className="font-mono text-5xl md:text-6xl font-medium text-transparent [-webkit-text-stroke:1px_theme(colors.border)] leading-none" data-id="element-65">
                  {item.number}
                </span>
                <h3 className="mt-4 font-heading text-2xl md:text-3xl font-medium text-foreground tracking-tight" data-id="element-66">
                  {item.title}
                </h3>
                <p className="mt-4 text-base text-muted-foreground leading-relaxed" data-id="element-67">
                  {item.body}
                </p>
              </div>
              <div className="bracket-frame relative overflow-hidden border border-border h-64 md:h-96" data-id="element-68">
                <img src={item.image} alt={item.alt} className="w-full h-full object-cover grayscale contrast-125" loading="lazy" data-id="element-69" />
                <div className="absolute inset-0 bg-background/50 mix-blend-multiply" data-id="element-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent" data-id="element-71" />
                <div className="absolute bottom-5 left-5 right-5" data-id="element-72">
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground bg-background/80 backdrop-blur-sm border border-signal/30 px-3 py-1.5" data-id="element-73">
                    <span className="inline-block h-1 w-1 bg-signal shrink-0" aria-hidden="true" data-id="element-74" />
                    {item.annotation}
                  </span>
                </div>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}